import { useState, useEffect } from 'react'
import { Loader2, FileText, Info } from 'lucide-react'
import LoadingSpinner from '../../ui/LoadingSpinner'
import ErrorMessage from '../../ui/ErrorMessage'
import SalonAnalysisTabs from './SalonAnalysisTabs'
import Top20Salons from './sub-reports/Top20Salons'
import StockExposition from './sub-reports/StockExposition'
import ParetoAnalysis from './sub-reports/ParetoAnalysis'
import MonthlyHeatmap from './sub-reports/MonthlyHeatmap'
import { useSalonAnalysisData } from './hooks/useSalonAnalysisData'
import { MONTH_NAMES, formatNumber, formatPercent } from './utils/chartHelpers'
import {
  ReportShell, HeaderButton, FilterBar, KpiStrip, Icon, fmt, theme,
} from '../../report-shell'

const OPERATOR_BREADCRUMB = {
  orange:  ['KAM-SFA', 'Orange', 'Analiza sprzedaży'],
  plus:    ['KAM-SFA', 'Plus', 'Analiza sprzedaży'],
  play:    ['KAM-SFA', 'Play', 'Analiza sprzedaży'],
  tmobile: ['KAM-SFA', 'T-Mobile', 'Analiza sprzedaży'],
  p4:      ['KAM-SFA', 'Play', 'Analiza sprzedaży'],
}
const OPERATOR_ACCENT = {
  orange:  '#ff7900',
  plus:    '#00a651',
  play:    '#7b2cbf',
  p4:      '#7b2cbf',
  tmobile: '#e20074',
}
const OPERATOR_NAMES = {
  orange: 'Orange', plus: 'Plus', play: 'Play', tmobile: 'T-Mobile', p4: 'Play',
}
const OPERATOR_PRODUCTS = {
  orange: 'Opaski Orange',
  plus: 'urządzeń Plus',
  play: 'urządzeń Play',
  tmobile: 'urządzeń T-Mobile',
  p4: 'urządzeń Play',
}

const formatDateForInput = (date) => date ? date.toISOString().split('T')[0] : ''

const SalonAnalysis = ({ operator }) => {
  const [activeTab, setActiveTab] = useState('top20')

  const {
    data, filteredSalonData, paretoStats, stockExpositionData,
    loading, stockExpositionLoading, error,
    analysisMode, selectedMonth, selectedYear, startDate, endDate, salonLimit,
    setAnalysisMode, setSelectedMonth, setSelectedYear, setStartDate, setEndDate, setSalonLimit,
    fetchStockExpositionData, exportParetoExcel, exportPDF, exportCSV,
  } = useSalonAnalysisData(operator)

  useEffect(() => {
    if (activeTab === 'stock') fetchStockExpositionData()
  }, [activeTab, fetchStockExpositionData])

  const opKey = (operator || '').toLowerCase()
  const operatorName = OPERATOR_NAMES[opKey] || operator
  const productName = OPERATOR_PRODUCTS[opKey] || 'urządzeń'
  const accentColor = OPERATOR_ACCENT[opKey]
  const breadcrumb = OPERATOR_BREADCRUMB[opKey] || ['KAM-SFA', operatorName, 'Analiza sprzedaży']
  const hasStockExposition = !['tmobile'].includes(opKey)

  // ─ FilterBar config ────────────────────────────────────────
  const currentYear = new Date().getFullYear()
  const years = [currentYear, currentYear - 1, currentYear - 2]

  const periods = [
    {
      type: 'segmented', key: 'analysisMode', label: 'Rodzaj analizy',
      value: analysisMode,
      options: [
        { value: 'monthly', label: 'Miesiąc' },
        { value: 'yearly',  label: 'Cały rok' },
        { value: 'custom',  label: 'Własny zakres' },
      ],
      onChange: setAnalysisMode,
    },
    {
      type: 'select', key: 'year', label: 'Rok',
      value: selectedYear, display: String(selectedYear),
      options: years.map(y => ({ value: y, label: String(y) })),
      onChange: (v) => setSelectedYear(parseInt(v)),
      allowClear: false,
      hideWhen: analysisMode === 'custom',
    },
    {
      type: 'select', key: 'month', label: 'Miesiąc',
      value: selectedMonth || 12,
      display: MONTH_NAMES[(selectedMonth || 12) - 1],
      options: MONTH_NAMES.map((n, i) => ({ value: i + 1, label: n })),
      onChange: (v) => setSelectedMonth(parseInt(v)),
      allowClear: false,
      hideWhen: analysisMode !== 'monthly',
    },
    {
      type: 'date', key: 'startDate', label: 'Data od',
      value: formatDateForInput(startDate),
      onChange: (v) => setStartDate(v ? new Date(v) : null),
      hideWhen: analysisMode !== 'custom',
    },
    {
      type: 'date', key: 'endDate', label: 'Data do',
      value: formatDateForInput(endDate),
      onChange: (v) => setEndDate(v ? new Date(v) : null),
      hideWhen: analysisMode !== 'custom',
    },
  ]

  // ─ KPI tiles ───────────────────────────────────────────────
  const totalSales = data?.total_sales || 0
  const activeSalons = data?.active_salons || 0
  const totalSalons = data?.total_salons || 0
  const pareto80 = paretoStats?.['80_20']
  const pareto70 = paretoStats?.['70_30']

  const kpiTiles = data ? [
    {
      key: 'sales',
      label: analysisMode === 'monthly' ? 'Sprzedaż w miesiącu' : 'Łączna sprzedaż',
      value: fmt.int(totalSales),
      unit: 'urządzeń',
    },
    {
      key: 'active', label: 'Aktywne salony',
      value: `${activeSalons}/${totalSalons}`,
      sub: totalSalons > 0
        ? `${formatPercent((activeSalons / totalSalons) * 100)} aktywności`
        : '0% aktywności',
      accent: theme.good,
    },
    {
      key: 'pareto80', label: 'Top salony (80%)',
      value: fmt.int(pareto80?.top_salons_count || 0),
      sub: pareto80 ? `${formatPercent(pareto80.salon_percentage)} salonów` : 'Brak danych',
      accent: theme.warn,
    },
    {
      key: 'pareto70', label: 'Top salony (70%)',
      value: fmt.int(pareto70?.top_salons_count || 0),
      sub: pareto70 ? `${formatPercent(pareto70.salon_percentage)} salonów` : 'Brak danych',
      accent: theme.warn,
    },
  ] : []

  return (
    <ReportShell
      title={`${operatorName} — Analiza sprzedaży per salon`}
      subtitle={`Sprzedaż, pokrycie i wydajność per pojedynczy salon — ${productName}.`}
      breadcrumb={breadcrumb}
      accentColor={accentColor}
      actions={
        <>
          <HeaderButton onClick={exportCSV} disabled={!filteredSalonData.length}>
            <Icon.download /> CSV
          </HeaderButton>
          {exportPDF && (
            <HeaderButton primary onClick={exportPDF} disabled={!filteredSalonData.length || loading}>
              {loading ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
              PDF
            </HeaderButton>
          )}
        </>
      }
      filterBar={<FilterBar periods={periods} />}
      kpiStrip={kpiTiles.length > 0 ? <KpiStrip tiles={kpiTiles} /> : null}
    >
      {loading && !data && (
        <div className="flex items-center justify-center min-h-96">
          <LoadingSpinner size="xl" />
        </div>
      )}
      {error && !data && (
        <ErrorMessage message={error} onRetry={() => window.location.reload()} />
      )}

      {!error && data && (
        <div className="space-y-6">
          {/* Period Info */}
          {data.analysis_period && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 14px', borderRadius: 8,
              background: '#eef2ff', border: '1px solid #c7d2fe',
              fontSize: 12.5, color: '#4338ca',
            }}>
              <Info size={16} />
              <span><strong>Okres analizy:</strong> {data.analysis_period}</span>
            </div>
          )}

          <SalonAnalysisTabs
            activeTab={activeTab}
            onChange={setActiveTab}
            analysisMode={analysisMode}
            hasStockExposition={hasStockExposition}
          />

          <div>
            {activeTab === 'top20' && (
              <Top20Salons
                data={data} operator={operator}
                analysisMode={analysisMode}
                analysisPeriod={data.analysis_period}
              />
            )}
            {activeTab === 'stock' && hasStockExposition && (
              <StockExposition
                data={stockExpositionData} operator={operator}
                loading={stockExpositionLoading}
                onFetchData={fetchStockExpositionData}
              />
            )}
            {activeTab === 'pareto' && (
              <ParetoAnalysis
                data={data} paretoStats={paretoStats} operator={operator}
                onExportExcel={exportParetoExcel}
              />
            )}
            {activeTab === 'heatmap' && analysisMode === 'yearly' && (
              <MonthlyHeatmap
                data={data} operator={operator}
                salonLimit={salonLimit}
                onSalonLimitChange={setSalonLimit}
              />
            )}
          </div>
        </div>
      )}
    </ReportShell>
  )
}

export default SalonAnalysis
